import { createFlagClient, createNoopFlagClient } from '@apptegy/ld-feature-flags';
import { provideFlags } from '@apptegy/ld-feature-flags/nuxt';
import { useCmsAiStore } from '../stores/cmsAiStore.js';

// The public site has no signed-in user, but existing LaunchDarkly rules still target
// `user.client_id`. A constant user key keeps the transitional multi-context shape so those
// rules keep matching until they are rewritten to target `organization.key` (EI-70, step 3).
const PUBLIC_SITE_USER_KEY = 'cms-public';

/**
 * Provides the LaunchDarkly flag client to the app as `$ld`, or a no-op client when flags cannot
 * be evaluated for the current organization.
 * @param {import('#app').NuxtApp} nuxtApp - The Nuxt app instance the client is provided to.
 * @returns {Promise<void>} Resolves once a flag client has been provided.
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  const clientId = useCmsAiStore().clientId;
  const ldClientKey = useRuntimeConfig().public.LAUNCH_DARKLY_KEY;

  if (!ldClientKey || !clientId) {
    provideFlags(nuxtApp, createNoopFlagClient());
    return;
  }

  // Consumers assume $ld is always present, so an unexpected failure must still
  // leave a no-op client behind rather than skipping provideFlags entirely.
  try {
    const client = await createFlagClient({
      clientKey: ldClientKey,
      context: { userId: PUBLIC_SITE_USER_KEY, clientId },
    });
    provideFlags(nuxtApp, client);
  } catch (error) {
    console.error(error);
    provideFlags(nuxtApp, createNoopFlagClient());
  }
});
